// index.tsx
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import { Button } from '@mui/material'
import DialogBox from '../../DialogBox'

export interface ExternalRedirectModalProps {
  /** Whether the dialog is open */
  open: boolean
  /** Absolute URL that the user will be sent to */
  url: string
  /** Callback when the user cancels */
  onCancel: () => void
  /** Optional callback to override default navigation behaviour */
  onContinue?: () => void
  'data-testid'?: string
}

/**
 * Centralised modal used whenever the UI needs to notify the user that they’re
 * about to follow a link to an external website.
 */
export default function ExternalRedirectModal({
  open,
  url,
  onCancel,
  onContinue,
  'data-testid': dataTestId
}: ExternalRedirectModalProps): JSX.Element {
  const hostname = (() => {
    try {
      return new URL(url).hostname
    } catch {
      return url
    }
  })()

  const handleContinue = (): void => {
    if (onContinue) {
      onContinue()
    } else {
      window.location.href = url
    }
  }

  return (
    <DialogBox
      open={open}
      title="External Link"
      description={`The link you selected will take you to an external site (${hostname}) in a new tab. Do you want to continue?`}
      actions={(
        <>
          <Button variant="text" onClick={onCancel} data-testid={`${dataTestId}-cancel`}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleContinue} data-testid={`${dataTestId}-continue`}>
            Continue
          </Button>
        </>
      )}
      data-testid={dataTestId}
      fullWidth
    />
  )
}

ExternalRedirectModal.defaultProps = {
  'data-testid': 'external-redirect-modal',
  onContinue: undefined
}
